import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Check current session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setProfile(null);
      }
    });

    // Listen for profile updates (custom event)
    const handleProfileUpdate = () => {
      if (user?.id) {
        fetchProfile(user.id);
      }
    };
    
    window.addEventListener('profile-updated', handleProfileUpdate);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener('profile-updated', handleProfileUpdate);
    };
  }, [user?.id]);

  const fetchProfile = async (userId: string) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    
    if (data) {
      console.log('Profile fetched:', data);
      setProfile(data);
    } else if (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setShowDropdown(false);
    navigate('/');
  };

  const isActive = (path: string) => {
    if (path.includes('#')) {
      const hash = path.substring(path.indexOf('#'));
      return location.pathname === '/' && location.hash === hash;
    }
    return location.pathname === path;
  };

  const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>, hash: string) => {
    e.preventDefault();
    
    if (location.pathname !== '/') {
      // Navigate to home first, then scroll
      navigate('/');
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      // Already on home page, just scroll
      const element = document.querySelector(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const getFirstName = () => {
    if (profile?.full_name) {
      return profile.full_name.split(' ')[0];
    }
    return user?.email?.split('@')[0] || 'User';
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <img 
              src="https://static.readdy.ai/image/9910db4e977c4892bacf08542838a807/407c1331b92070399587dede53135c16.png" 
              alt="OhMyGAWD Logo" 
              className="h-12 w-12 object-contain transition-transform group-hover:scale-110"
            />
            <span className={`font-serif text-2xl font-bold transition-colors ${
              scrolled ? 'text-gray-900' : 'text-[#fefefe]'
            }`}>
              OhMyGAWD
            </span>
          </Link>

          <div className="flex items-center gap-8">
            <a
              href="#features"
              onClick={(e) => handleAnchorClick(e, '#features')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isActive('/#features') 
                  ? scrolled ? 'text-purple-600' : 'text-cyan-300'
                  : scrolled ? 'text-gray-900 hover:text-purple-600' : 'text-[#fefefe] hover:text-cyan-300'
              }`}
            >
              Features
            </a>
            <a
              href="#pricing"
              onClick={(e) => handleAnchorClick(e, '#pricing')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isActive('/#pricing')
                  ? scrolled ? 'text-purple-600' : 'text-cyan-300'
                  : scrolled ? 'text-gray-900 hover:text-purple-600' : 'text-[#fefefe] hover:text-cyan-300'
              }`}
            >
              Pricing
            </a>
            <Link
              to="/about"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                isActive('/about')
                  ? scrolled ? 'text-purple-600' : 'text-cyan-300'
                  : scrolled ? 'text-gray-900 hover:text-purple-600' : 'text-[#fefefe] hover:text-cyan-300'
              }`}
            >
              About
            </Link>
            <Link
              to="/oracle"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                isActive('/oracle')
                  ? scrolled ? 'text-purple-600' : 'text-cyan-300'
                  : scrolled ? 'text-gray-900 hover:text-purple-600' : 'text-[#fefefe] hover:text-cyan-300'
              }`}
            >
              Oracle
            </Link>
            <Link
              to="/support"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                isActive('/support')
                  ? scrolled ? 'text-purple-600' : 'text-cyan-300'
                  : scrolled ? 'text-gray-900 hover:text-purple-600' : 'text-[#fefefe] hover:text-cyan-300'
              }`}
            >
              Support
            </Link>

            {user ? (
              <div className="relative">
                <button
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-bold rounded-full hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-user-star-line text-base"></i>
                  <span>{getFirstName()}</span>
                  <i className={`ri-arrow-down-s-line text-base transition-transform ${showDropdown ? 'rotate-180' : ''}`}></i>
                </button>

                <AnimatePresence>
                  {showDropdown && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden"
                    >
                      <div className="p-4 border-b border-gray-100">
                        <p className="font-semibold text-gray-900">{profile?.full_name || 'User'}</p>
                        <p className="text-xs text-gray-500 mt-1">{user.email}</p>
                        <div className="mt-2 px-3 py-1.5 bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg">
                          <p className="text-xs font-semibold text-purple-900">
                            {profile?.subscription_tier === 'free' ? 'Free Plan' : 
                             profile?.subscription_tier === 'basic' ? 'Basic Plan' :
                             profile?.subscription_tier === 'premium' ? 'Premium Plan' : 'Plan'}
                          </p>
                          <p className="text-xs text-purple-700 mt-0.5">
                            {profile?.queries_used || 0} / {profile?.queries_limit || 0} queries used
                          </p>
                        </div>
                      </div>

                      <div className="py-2">
                        <Link
                          to="/oracle"
                          onClick={() => setShowDropdown(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-purple-50 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-sparkling-line text-base"></i>
                          <span>My Oracle</span>
                        </Link>
                        <button
                          onClick={handleSignOut}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-logout-box-line text-base"></i>
                          <span>Sign Out</span>
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <>
                <Link
                  to="/auth"
                  className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-bold rounded-full hover:shadow-lg transition-all whitespace-nowrap"
                >
                  Sign In
                </Link>
                <button className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-pink-500 text-white text-sm font-medium rounded-full hover:shadow-lg hover:shadow-purple-500/50 transition-all whitespace-nowrap">
                  Subscribe Now
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </motion.nav>
  );
};

export default Navbar;